/**
 * @file Custom prints related utilities.
 */

import get from 'lodash.get';
import flatten from 'lodash.flatten';

import {
  CUSTOM_PRINT_DELIVERY_TYPE,
  CUSTOM_PRINT_PRINTING_IMAGE_SIZE,
  CUSTOM_PRINT_SUBTYPE,
  CUSTOM_PRINT_TYPE,
} from '../constants/pro-custom-prints';
import { getThumbBySize } from './photo';
import { getIdentityLastName, getIdentityName } from './identity';
import { CARD_ELEMENT_TYPE } from '../services/cards/constants';

/**
 * Returns an album custom print's price (optionally the discounted one).
 *
 * @param {Object} albumCustomPrint - The album custom print.
 * @param {boolean} [discounted] - Whether to return the discount price or not.
 * @returns {number}
 */
const getAlbumCustomPrintPrice = (albumCustomPrint, discounted) => {
  if (!albumCustomPrint) {
    return undefined;
  }

  const discountedPrice = albumCustomPrint?.discount_price?.value;

  if (discounted) {
    return discountedPrice;
  }

  return albumCustomPrint?.price?.value;
};

/**
 * Checks whether an album custom print has a discount.
 *
 * @param {Object} albumCustomPrint - The album custom print.
 * @returns {boolean}
 */
const albumCustomPrintHasDiscount = albumCustomPrint => {
  if (!albumCustomPrint) {
    return false;
  }

  const price = getAlbumCustomPrintPrice(albumCustomPrint);
  const discountedPrice = getAlbumCustomPrintPrice(albumCustomPrint, true);

  return price > discountedPrice;
};

/**
 * Returns the price a custom print should display if it's part of
 * a subscription bundle (i.e. When purchasing a subscription and the
 * custom print has a discount price, we always charge the discount price).
 *
 * @param {Object} albumCustomPrint - The album custom print.
 * @returns {number} - The discounted price if it exists else the normal price.
 */
const getAlbumCustomPrintBundlePrice = albumCustomPrint => {
  if (!albumCustomPrint) {
    return undefined;
  }

  const price = getAlbumCustomPrintPrice(albumCustomPrint);
  const discountedPrice = getAlbumCustomPrintPrice(albumCustomPrint, true);

  return discountedPrice != null ? discountedPrice : price;
};

/**
 * Returns an album custom print's name.
 *
 * @param {Object} albumCustomPrint - The album custom print's name.
 * @returns {string}
 */
const getAlbumCustomPrintName = albumCustomPrint => {
  return albumCustomPrint?.custom_print?.name ?? '';
};

/**
 * Returns a purchased custom print's name.
 *
 * @param {Object} purchasedCustomPrint - The purchased custom print.
 * @param {string} purchasedCustomPrint.name
 * @returns {string} - The name.
 */
const getPurchasedCustomPrintName = purchasedCustomPrint => {
  return purchasedCustomPrint?.name ?? '';
};

/**
 * Returns the first album custom print (mainly used for PPD purchase).
 *
 * At the time of the implementation only one custom print per
 * album is supported to be purchased.
 *
 * @param {Object} album - The album.
 * @returns {Object} - The first album custom print of the album.
 */
const getAlbumCustomPrint = album => {
  if (!album) {
    return undefined;
  }

  const [albumCustomPrint] = get(album, 'custom_prints.records', []);
  return albumCustomPrint;
};

/**
 * Determines whether an album custom print can be configured by the user
 * or not by checking if the selected identity has enough matched photos.
 *
 * @param {Object} albumCustomPrint - The album custom print.
 * @returns {boolean}
 */
const canConfigAlbumCustomPrint = albumCustomPrint => {
  if (!albumCustomPrint) {
    return false;
  }

  return (
    get(
      albumCustomPrint,
      'selected_identity.matched_photos_extended.total',
      0,
    ) >= albumCustomPrint.photos_no
  );
};

/**
 * Determines whether a custom print  requires an account
 * address in order to be configured.
 *
 * @param {Object} albumCustomPrint - The album custom print record.
 * @returns {boolean}
 */
const albumCustomPrintRequiresAddressToConfig = albumCustomPrint => {
  if (!albumCustomPrint) {
    return false;
  }

  // For purchased custom prints
  const deliveryTypeIsShipping =
    albumCustomPrint.delivery_type === CUSTOM_PRINT_DELIVERY_TYPE.SHIPPING;

  // For album custom prints (and primarily collages)
  const shippingRequiredAndEnabled =
    albumCustomPrint.shipping_required && albumCustomPrint.shipping_enabled;

  return (
    (deliveryTypeIsShipping || !!shippingRequiredAndEnabled) &&
    !albumCustomPrint.account_address
  );
};

/**
 * Determines whether an album custom print has shipping as a user
 * selectable option rather than required.
 *
 * @param {Object} albumCustomPrint - The album custom print.
 * @returns {boolean}
 */
const canSelectAlbumCustomPrintShipping = albumCustomPrint => {
  if (!albumCustomPrint) {
    return false;
  }

  return (
    get(albumCustomPrint, 'shipping_price.value') > 0 &&
    get(albumCustomPrint, 'shipping_enabled') &&
    !get(albumCustomPrint, 'shipping_required')
  );
};

/**
 * Returns the sum total of shipping price of all album custom prints.
 *
 * @param {Array<Object>} albumCustomPrints - The album custom prints.
 * @returns {number} - The total shipping price.
 */
const getAlbumCustomPrintsTotalShippingPrice = albumCustomPrints => {
  if (!albumCustomPrints || !albumCustomPrints.length) {
    return 0;
  }

  return albumCustomPrints.reduce((sum, albumCustomPrint) => {
    return sum + get(albumCustomPrint, 'shipping_price.value');
  }, 0);
};

const getAlbumCustomPrintPreviewImageUrl = albumCustomPrint => {
  return (
    get(albumCustomPrint, 'preview_image.url', '') ||
    get(albumCustomPrint, 'custom_print.preview_image.url', '') ||
    null
  );
};

/**
 * Determines whether a Waldogram (PurchasedCustomPrint) is to be printed onsite.
 *
 * @param {Object} customPrint - The PurchasedCustomPrint.
 * @returns {boolean}
 */
const isOnsitePrintableWaldogram = customPrint => {
  if (!customPrint) {
    return false;
  }

  return (
    get(customPrint, 'custom_print_type') === CUSTOM_PRINT_TYPE.WALDOGRAM &&
    !get(customPrint, 'waldogram.external_shipping_address') &&
    !get(customPrint, 'custom_print.account_address')
  );
};

/**
 * Determines whether a Collage (PurchasedCustomPrint) is to be printed onsite.
 *
 * @param {Object} customPrint - The PurchasedCustomPrint with type COLLAGE.
 * @returns {boolean}
 */
const isOnsitePrintableCollage = customPrint => {
  if (
    !customPrint ||
    customPrint.custom_print_type !== CUSTOM_PRINT_TYPE.COLLAGE
  ) {
    return false;
  }

  return !customPrint.shipping_required && !customPrint.shipping_enabled;
};

/**
 * Determines whether a Collage (PurchasedCustomPrint) is to be configured by the vendor
 * account (PRO).
 *
 * @param {Object} customPrint - The PurchasedCustomPrint with type COLLAGE.
 * @returns {boolean}
 */
const isVendorConfigurableCollage = customPrint => {
  if (!customPrint) {
    return false;
  }

  return customPrint.vendor_configuration;
};

/**
 * Determines whether a PurchasedCustomPrint is configured.
 *
 * @param {Object} customPrint - The PurchasedCustomPrint.
 * @returns {boolean}
 */
const isCustomPrintConfigured = customPrint => {
  if (!customPrint) {
    return false;
  }

  const photosNo = customPrint.photos_no;
  const selectedPhotosNo = customPrint.photos.records.length;

  return photosNo === selectedPhotosNo;
};

/**
 * Returns CustomPrints by type.
 *
 * @param {CUSTOM_PRINT_TYPE} type - The custom print type to return.
 * @param {Array<Object>} customPrints - The custom print records.
 * @returns {Array<Object>} - The custom prints of the specific type.
 */
const getCustomPrintsByType = (type, customPrints = []) => {
  if (!type || !customPrints) {
    return [];
  }

  return customPrints.filter(
    customPrint =>
      get(customPrint, 'custom_print_type') === type ||
      get(customPrint, 'custom_print.type') === type ||
      get(customPrint, 'type') === type,
  );
};

/**
 * Given an array of CustomPrints it filters out externally shipped WALDOGRAM custom prints.
 *
 * @param {Array<Object>} customPrints - The custom prints.
 * @returns {Array<Object>} - The non-externally shipped custom prints.
 */
const filterExternallyShippedWaldogramCustomPrints = customPrints => {
  if (!customPrints) {
    return [];
  }

  const waldogramCustomPrints = getCustomPrintsByType(
    CUSTOM_PRINT_TYPE.WALDOGRAM,
    customPrints,
  );

  return waldogramCustomPrints.filter(
    waldogramCustomPrint =>
      !get(waldogramCustomPrint, 'waldogram.external_shipping_address'),
  );
};

/**
 * Given an array of CustomPrints it returns onsite printable WALDOGRAM custom prints.
 *
 * @param {Array<Object>} customPrints - The custom prints.
 * @returns {Array<Object>} - The printable WALDOGRAM custom prints.
 */
const getPrintablePurhasedWaldograms = customPrints => {
  if (!customPrints) {
    return [];
  }

  return filterExternallyShippedWaldogramCustomPrints(customPrints).filter(
    waldogramCustomPrint =>
      !get(waldogramCustomPrint, 'custom_print.account_address') &&
      !!waldogramCustomPrint.print_file_url,
  );
};

/**
 * Given an array of CustomPrints it returns onsite printable COLLAGE custom prints.
 *
 * @param {Array<Object>} customPrints - The custom prints.
 * @returns {Array<Object>} - The printable COLLAGE custom prints.
 */
const getPrintablePurchasedCollages = customPrints => {
  if (!customPrints) {
    return [];
  }

  return customPrints.filter(customPrint => {
    return isOnsitePrintableCollage(customPrint) && customPrint.print_file_url;
  });
};

/**
 * Extract the URL of a purchased custom print photo record (which could
 * contain a Photo or an AssetImage).
 *
 * @param {Object} record - The photo record.
 * @param {IMAGE_SIZE_CODE} [size] - The image size.
 * @returns {string} - The URL.
 */
const getPurchasedCustomPrintPhotoUrl = (
  record,
  size = CUSTOM_PRINT_PRINTING_IMAGE_SIZE,
) => {
  if (!record) {
    return '';
  }

  if (record.asset_image) {
    return record.asset_image.url;
  }

  return getThumbBySize(record.photo.thumbnail_urls, size).url;
};

/**
 * Returns an array with all the selected photos from all provided custom prints.
 *
 * @param {Array<Object>} customPrints - The purchased custom prints.
 * @returns {Array<string>}
 */
const getPurchasedCustomPrintsPreloadUrls = customPrints => {
  const photoUrls = customPrints.map(customPrint =>
    customPrint.photos.records
      .map(photo => getPurchasedCustomPrintPhotoUrl(photo))
      .filter(Boolean),
  );

  const logos = customPrints
    .map(customPrint => get(customPrint, 'vendor_account.logo_image.url'))
    .filter(Boolean);

  return flatten([...photoUrls, ...[...new Set(logos)]]);
};

/**
 * Accepts an emoji codepoint string (space separated) and returns
 * a string to be used with the emoji CDN.
 *
 * @param {string} codepoint - The emoji codepoint as returned.
 * @returns {string} - The formatted emoji codepoint.
 */
const formatCustomPrintPhotoEmojiCodepoint = codepoint => {
  if (!codepoint) {
    return '';
  }

  const unicodeArray = codepoint.toLowerCase().split(' ');

  if (unicodeArray.length > 2) {
    return unicodeArray.join('-');
  }

  // Remove the U+FE0F invisible codepoint as it is only used in plain text
  // https://emojipedia.org/variation-selector-16/
  return unicodeArray.join('-').replace('-fe0f', '');
};

/**
 * Checks if a custom print is a Waldogram.
 *
 * @param {Object} customPrint - The custom print to check.
 * @returns {boolean}
 */
const isWaldogram = customPrint => {
  if (!customPrint) {
    return false;
  }

  return (
    getCustomPrintsByType(CUSTOM_PRINT_TYPE.WALDOGRAM, [customPrint]).length > 0
  );
};

/**
 * Returns the display name for an identity associated with a custom print
 * (PurchasedCustomPrint).
 *
 * @param {Object} customPrint - The PurchasedCustomPrint record.
 * @returns {string}
 */
const getCustomPrintIdentityDisplayName = customPrint => {
  if (!customPrint) {
    return '';
  }

  const name = getIdentityName(customPrint.identity);
  const waldogramName = get(customPrint, 'waldogram.identity_name');
  const lastName = getIdentityLastName(customPrint.identity);

  if ((!name || name.toLowerCase() === 'me') && waldogramName) {
    return `${waldogramName} ${lastName}`.trim();
  }

  if (name) {
    return `${name} ${lastName}`.trim();
  }

  return `${customPrint?.account?.name ?? ''}`.trim();
};

/**
 * Checks whether a CustomPrint (or AlbumCustomPrint) has caption enabled.
 *
 * @param {Object} customPrint The CustomPrint or AlbumCustomPrint.
 * @returns {boolean}
 */
const customPrintCaptionEnabled = customPrint => {
  if (!customPrint) {
    return false;
  }

  return (
    customPrint.custom_print_caption_enabled ??
    customPrint.custom_print?.custom_print_caption_enabled
  );
};

/**
 * Gets the minimum price for a given custom print
 *
 * @param {Object} customPrint The CustomPrint or AlbumCustomPrint.
 * @returns {Number}
 */
const getCustomPrintMinPrice = customPrint => {
  const customPrintSize =
    customPrint?.card?.card_side_groups?.records?.[0]?.card_sides?.records?.[0]
      ?.card_template?.print_size;

  const customPrintProduct =
    customPrint?.card?.card_side_groups?.records?.[0]?.card_sides?.records?.[0]
      ?.card_template?.print_product;

  const printProductSize = (customPrintProduct?.sizes?.records ?? []).find(
    ppSize => ppSize.print_size.id === customPrintSize?.id,
  );

  if (customPrint.subtype === CUSTOM_PRINT_SUBTYPE.SPECIALTY_PRINT) {
    return customPrint?.price?.value;
  }

  return (
    (printProductSize?.min_price ?? printProductSize?.price)?.value ?? 1000
  );
};

/**
 * Gets the amount of photos for a given custom print
 *
 * @param {Object} customPrint The CustomPrint or AlbumCustomPrint.
 * @returns {number}
 */
const getCustomPrintPhotosNo = customPrint => {
  // Return the prev. arbitrary number if need be
  if (!customPrint?.card) {
    return 2;
  }

  const { card } = customPrint;
  const cardSideGroup = card.card_side_groups?.records?.[0];
  const cardSide = cardSideGroup.card_sides?.records?.[0];

  if (!cardSide) {
    return 2;
  }

  const configurations = cardSide.card_element_configurations;
  const photoElements = cardSide.card_template.card_elements.filter(
    elem => elem.type === CARD_ELEMENT_TYPE.PHOTO,
  );

  return photoElements.filter(elem => {
    const elementConfiguration = configurations.find(
      config => config.card_element.id === elem.id,
    );

    return !elementConfiguration || elementConfiguration.is_editable;
  }).length;
};

const getCustomPrintPrintPartner = customPrint => {
  const customPrintProduct =
    customPrint?.card?.card_side_groups?.records?.[0]?.card_sides?.records?.[0]
      ?.card_template?.print_product;

  return customPrintProduct?.print_partner;
};

export {
  getAlbumCustomPrintPrice,
  albumCustomPrintHasDiscount,
  getAlbumCustomPrintBundlePrice,
  getAlbumCustomPrintName,
  getAlbumCustomPrint,
  getPurchasedCustomPrintName,
  canConfigAlbumCustomPrint,
  canSelectAlbumCustomPrintShipping,
  getAlbumCustomPrintsTotalShippingPrice,
  getAlbumCustomPrintPreviewImageUrl,
  getCustomPrintsByType,
  getPrintablePurhasedWaldograms,
  getPurchasedCustomPrintPhotoUrl,
  formatCustomPrintPhotoEmojiCodepoint,
  isOnsitePrintableWaldogram,
  isOnsitePrintableCollage,
  isVendorConfigurableCollage,
  filterExternallyShippedWaldogramCustomPrints,
  isWaldogram,
  getPurchasedCustomPrintsPreloadUrls,
  albumCustomPrintRequiresAddressToConfig,
  isCustomPrintConfigured,
  getPrintablePurchasedCollages,
  getCustomPrintIdentityDisplayName,
  customPrintCaptionEnabled,
  getCustomPrintMinPrice,
  getCustomPrintPhotosNo,
  getCustomPrintPrintPartner,
};
